import { useEffect, useRef, useState, type ReactNode } from 'react';
import {
  remoteIssueTypeSchema,
  ruleVocabularySchema,
  type LineItemRule,
  type LineItemTarget,
  type RuleVocabulary,
  type LineItemTargetOption,
  type RemoteIssueType,
  type SetLineItemTarget,
} from '@forecastify/shared';
import { Button, Icon, Popover, Segmented, Select, Switch, Tooltip } from '@forecastify/ui';
import { useT } from '../../i18n';
import { ProviderLogo } from '../integrations/provider-logo';
import { useSession } from '../../session';
import { RuleEditor, type RuleOptions } from './rule-editor';

/**
 * ⚠️ **An Epic, by its name.** The port carries no hierarchy level, and the hint below is advice
 * rather than a gate — a false negative costs one sentence, a false positive one sentence too. HU
 * Jira sites call it *Eposz*.
 */
const EPIC = /epic|eposz/iu;

/**
 * ux-pass-2 · the issue types the target's remote project can create, read with the instance's
 * credential (`GET /integrations/:id/remote-projects/:remoteProjectId/issue-types`).
 *
 * ⚠️ **`null` is "not loaded", `'failed'` is "asked and could not"**, so the picker can say which —
 * an empty list would read as a project with no types at all.
 */
function useIssueTypes(
  instanceId: string,
  remoteProjectId: string,
  enabled: boolean,
): readonly RemoteIssueType[] | null | 'failed' {
  const session = useSession();
  const [types, setTypes] = useState<readonly RemoteIssueType[] | null | 'failed'>(null);

  useEffect(() => {
    if (!enabled || instanceId === '' || remoteProjectId === '') return;
    /* An object, so the closure reads it rather than a narrowed `true`. */
    const live = { current: true };
    setTypes(null);
    void (async () => {
      const response = await session
        .request(
          `/integrations/${encodeURIComponent(instanceId)}/remote-projects/${encodeURIComponent(
            remoteProjectId,
          )}/issue-types`,
        )
        .catch(() => null);
      const body: unknown = response?.ok === true ? await response.json().catch(() => null) : null;
      const parsed = remoteIssueTypeSchema
        .array()
        .safeParse((body as { data?: unknown } | null)?.data);
      if (live.current) setTypes(parsed.success ? parsed.data : 'failed');
    })();
    return () => {
      live.current = false;
    };
    /* eslint-disable-next-line react-hooks/exhaustive-deps -- `session` is a fresh object every
       render (C20); the pair and the gate are what change. */
  }, [enabled, instanceId, remoteProjectId]);

  return types;
}

/**
 * 4 Nov 2026 · the Jira lists of a remote project only PICKED — what the draft rule below the
 * pickers offers before *Mentés* (`GET /integrations/:id/remote-projects/:id/vocabulary`). `null`
 * until it lands, and an empty one when it cannot: the free-text fields work either way.
 */
function usePickedVocabulary(
  instanceId: string,
  remoteProjectId: string,
  enabled: boolean,
): RuleVocabulary | null {
  const session = useSession();
  const [vocabulary, setVocabulary] = useState<RuleVocabulary | null>(null);

  useEffect(() => {
    setVocabulary(null);
    if (!enabled || instanceId === '' || remoteProjectId === '') return;
    const live = { current: true };
    void (async () => {
      const response = await session
        .request(
          `/integrations/${encodeURIComponent(instanceId)}/remote-projects/${encodeURIComponent(
            remoteProjectId,
          )}/vocabulary`,
        )
        .catch(() => null);
      const body: unknown = response?.ok === true ? await response.json().catch(() => null) : null;
      const parsed = ruleVocabularySchema.safeParse((body as { data?: unknown } | null)?.data);
      if (live.current) {
        setVocabulary(
          parsed.success ? parsed.data : { components: [], fixVersions: [], epics: [] },
        );
      }
    })();
    return () => {
      live.current = false;
    };
    /* eslint-disable-next-line react-hooks/exhaustive-deps -- `session` is a fresh object every
       render (C20's ten-requests bug); the pair and the gate are what change. */
  }, [instanceId, remoteProjectId, enabled]);

  return vocabulary;
}

/** What *Mentés* sends — only the parts that changed, in ONE patch so they cannot disagree. */
export interface SyncSettingsPatch {
  readonly target?: SetLineItemTarget | null;
  readonly assignment?: 'manual' | 'rule';
  readonly rule?: LineItemRule;
}

/** The card's draft: everything it shows, held until *Mentés*. */
interface Draft {
  readonly sync: boolean;
  readonly instanceId: string;
  readonly remoteProjectId: string;
  readonly writesEnabled: boolean;
  readonly issueTypeId: string | null;
  readonly issueTypeName: string | null;
  readonly assignment: 'manual' | 'rule';
  readonly rule: LineItemRule;
}

const draftOf = (
  target: LineItemTarget | null,
  assignment: 'manual' | 'rule',
  rule: LineItemRule,
): Draft => ({
  sync: target !== null,
  instanceId: target?.instanceId ?? '',
  remoteProjectId: target?.remoteProjectId ?? '',
  writesEnabled: target?.writesEnabled ?? false,
  issueTypeId: target?.issueTypeId ?? null,
  issueTypeName: target?.issueTypeName ?? null,
  assignment,
  rule,
});

/** The patch that turns `saved` into `draft` — `{}` when they are the same. */
function patchOf(saved: Draft, draft: Draft): SyncSettingsPatch {
  const same = (a: unknown, b: unknown): boolean => JSON.stringify(a) === JSON.stringify(b);
  const targetOf = (one: Draft) =>
    one.sync
      ? [one.instanceId, one.remoteProjectId, one.writesEnabled, one.issueTypeId, one.issueTypeName]
      : null;
  return {
    ...(same(targetOf(saved), targetOf(draft))
      ? {}
      : {
          target: draft.sync
            ? {
                instanceId: draft.instanceId,
                remoteProjectId: draft.remoteProjectId,
                writesEnabled: draft.writesEnabled,
                issueTypeId: draft.issueTypeId,
                issueTypeName: draft.issueTypeName,
              }
            : null,
        }),
    ...(saved.assignment === draft.assignment ? {} : { assignment: draft.assignment }),
    ...(same(saved.rule, draft.rule) ? {} : { rule: draft.rule }),
  };
}

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ ⚠️⚠️ 5 Nov 2026 · HOW TASKS ARRIVE — ONE CARD, ONE *MENTÉS*.               │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * The product owner's layout, top to bottom:
 *
 * 1. **Szinkronizálás hibakövetőből** — a switch, and while it is on, the integration beside it
 *    (shown, not picked, when the workspace has only one) and that integration's project.
 * 2. **Kézzel | Szabály szerint** — a small toggle, not a full-width one.
 * 3. **The conditions, only by rule** — *minden | bármelyik* a toggle too, since it has two values.
 * 4. **A footer**: *Szinkronizálás most* and the counts on the left, *Mégsem* and *Mentés* on the
 *    right.
 *
 * ⚠️⚠️ **NOTHING SAVES UNTIL *MENTÉS*, AND *MENTÉS* IS ONE PATCH.** A target, the mode and the rule
 * each re-route the project's tasks (`rerouteTargets`), so saving them one at a time routed every
 * issue of a tracker project onto the line item under an empty rule before its filters arrived —
 * the 21 Oct and 4 Nov rows. Held together and sent together, the first routing pass is the one
 * that was meant. The writes switch and the issue type ride in the same draft, so the card has one
 * way to save rather than two.
 *
 * ⚠️ **A reader sees the same card, read-only and without the footer's buttons.** A closed line
 * item (R1) keeps everything that could bring work onto it read-only; only *Kézzel* stays open.
 *
 * ⚠️⚠️ **NOT DRAWN BY THE MOCKUP**, which has no per-line-item targeting; the controls are the kit's
 * `Switch`, `Select` and `Segmented` at the rail's 30px, per `docs/design-system/`.
 */
export function LineItemSyncCard({
  target,
  assignment,
  rule,
  options,
  ruleOptions,
  canManage,
  canConfigure,
  onSave,
  footer,
}: {
  readonly target: LineItemTarget | null;
  readonly assignment: 'manual' | 'rule';
  readonly rule: LineItemRule;
  readonly options: readonly LineItemTargetOption[];
  readonly ruleOptions: RuleOptions;
  /** May change the settings at all — `lineitem.manage`. */
  readonly canManage: boolean;
  /** May bring work onto the line item — false on a closed one (R1). */
  readonly canConfigure: boolean;
  /** Resolves once the write has landed (or failed — the draft is kept then). */
  readonly onSave: (patch: SyncSettingsPatch) => unknown;
  /** The footer's left side: *Szinkronizálás most* and the counts. */
  readonly footer?: ReactNode;
}) {
  const t = useT();
  const saved = draftOf(target, assignment, rule);
  const savedKey = JSON.stringify(saved);
  const [draft, setDraft] = useState<Draft>(saved);
  const [busy, setBusy] = useState(false);

  /* ⚠️ A saved value that changes underneath replaces the draft only while nobody has touched it —
     a live reload landing mid-edit cannot undo the edit. */
  const seen = useRef(savedKey);
  useEffect(() => {
    const before = seen.current;
    seen.current = savedKey;
    setDraft((current) => (JSON.stringify(current) === before ? saved : current));
    /* eslint-disable-next-line react-hooks/exhaustive-deps -- keyed on the CONTENT: the parent
       builds fresh objects every render. */
  }, [savedKey]);

  const patch = patchOf(saved, draft);
  const dirty = Object.keys(patch).length > 0;
  /* An integration with no project chosen names nothing to collect from. */
  const valid = !draft.sync || (draft.instanceId !== '' && draft.remoteProjectId !== '');
  const edit = (part: Partial<Draft>): void => {
    setDraft((current) => ({ ...current, ...part }));
  };

  const pickedProject = draft.sync && draft.remoteProjectId !== '';
  const onSavedTarget =
    target !== null &&
    draft.instanceId === target.instanceId &&
    draft.remoteProjectId === target.remoteProjectId;
  const issueTypes = useIssueTypes(
    draft.instanceId,
    draft.remoteProjectId,
    canConfigure && pickedProject,
  );
  /* A project other than the saved one brings its own Jira lists for the conditions. */
  const vocabulary = usePickedVocabulary(
    draft.instanceId,
    draft.remoteProjectId,
    canConfigure && pickedProject && !onSavedTarget,
  );
  const conditionOptions: RuleOptions =
    !pickedProject || onSavedTarget
      ? ruleOptions
      : {
          ...ruleOptions,
          components: vocabulary?.components ?? [],
          fixVersions: vocabulary?.fixVersions ?? [],
          epics: vocabulary?.epics ?? [],
        };

  /* The integrations offered — and a saved one no longer offered still names itself. */
  const instances: readonly LineItemTargetOption[] = [
    ...options,
    ...(target !== null && !options.some((option) => option.instanceId === target.instanceId)
      ? [
          {
            instanceId: target.instanceId,
            instanceName: target.instanceName,
            extensionId: target.extensionId,
            remoteProjects: [],
          },
        ]
      : []),
  ];
  const instance = instances.find((option) => option.instanceId === draft.instanceId) ?? null;
  /* ⚠️ One integration in the workspace is shown, not picked — there is nothing to choose. */
  const onlyInstance = instances.length === 1 ? instances[0] : undefined;

  const projectChoices = (instance?.remoteProjects ?? []).map((project) => ({
    value: project.remoteProjectId,
    label: project.reachable
      ? `${project.key} · ${project.name}`
      : `${project.key} · ${project.name} (${t('lineItem.target.unreachable')})`,
    /* ⚠️ Shown and unpickable: a project the credential cannot see would sync nothing. */
    disabled: !project.reachable && project.remoteProjectId !== target?.remoteProjectId,
  }));
  if (
    target !== null &&
    instance?.instanceId === target.instanceId &&
    !projectChoices.some((choice) => choice.value === target.remoteProjectId)
  ) {
    projectChoices.unshift({
      value: target.remoteProjectId,
      label: `${target.remoteKey} · ${target.remoteName}`,
      disabled: false,
    });
  }

  /* The pencil's popover, and what its tooltip says the type is. */
  const [typeOpen, setTypeOpen] = useState(false);
  const typeSentence =
    draft.issueTypeName === null
      ? t('lineItem.target.noIssueType')
      : t('lineItem.target.issueType', { type: draft.issueTypeName });

  const save = (): void => {
    setBusy(true);
    void Promise.resolve(onSave(patch)).finally(() => {
      setBusy(false);
    });
  };

  return (
    <div className="@container mt-1.5 overflow-hidden rounded-lg border">
      {/* ⚠️⚠️ 5 Nov 2026 · LABELLED ROWS, AND THE CONDITIONS INSET UNDER THEIR MODE. The product
          owner: *"no hierarchy, two pills after each other, random many row section right after"*.
          Each setting has its name in a muted column on the left — a settings form, not a stack of
          pills — and the conditions are a box of their own under *Szabály szerint*, which is what
          they belong to. ⚠️ A NARROW CARD — a phone, or the line item's sidebar — stacks each label
          over its control: the breakpoint is the card's own width (`@container`), not the
          screen's, because in the sidebar the screen was wide and the controls were drawn over
          each other. ⚠️ The project group has NO `min-w`: a flex item's default minimum is its content (the name plus the picker's 160px), so *Írás a hibakövetőbe* wraps rather than
          overlapping it. */}
      <div className="grid grid-cols-[104px_minmax(0,1fr)] items-start gap-x-3 gap-y-3 p-3 @max-[560px]:grid-cols-1 @max-[560px]:gap-y-1.5">
        {/* 1 · the source — not drawn at all where the workspace has no tracker. */}
        {/* 5 Nov 2026 · the row's name says what the switch does — the switch carries no text of its
            own, which is what let *Írás a hibakövetőbe* fit on the same line. */}
        {instances.length > 0 ? <RowLabel>{t('lineItem.target.title')}</RowLabel> : null}
        {instances.length > 0 ? (
          <div className="flex min-w-0 flex-col gap-2">
            <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
              <span className="flex h-[30px] shrink-0 items-center">
                <Switch
                  checked={draft.sync}
                  disabled={!canConfigure}
                  label={t('lineItem.target.panelLabel')}
                  onCheckedChange={(sync) => {
                    if (!sync) {
                      edit({ sync });
                      return;
                    }
                    /* Back on: the saved target if there is one, else the only integration. */
                    edit(
                      target !== null
                        ? draftOf(target, draft.assignment, draft.rule)
                        : { sync, instanceId: onlyInstance?.instanceId ?? draft.instanceId },
                    );
                  }}
                />
              </span>
              {draft.sync ? (
                <div className="flex flex-1 items-center gap-1.5">
                  {onlyInstance !== undefined ? (
                    <span className="flex h-[30px] shrink-0 items-center gap-1.5 px-2 text-[12.5px]">
                      <ProviderLogo provider={onlyInstance.extensionId} size={13} />
                      <span className="truncate">{onlyInstance.instanceName}</span>
                    </span>
                  ) : (
                    <span className="w-[180px] shrink-0">
                      <Select
                        aria-label={t('lineItem.target.instance')}
                        className="h-[30px] text-[12.5px]"
                        disabled={!canConfigure}
                        value={draft.instanceId}
                        options={[
                          ...(draft.instanceId === ''
                            ? [
                                {
                                  value: '',
                                  label: t('lineItem.target.pickInstance'),
                                  disabled: true,
                                },
                              ]
                            : []),
                          ...instances.map((option) => ({
                            value: option.instanceId,
                            label: option.instanceName,
                            media: <ProviderLogo provider={option.extensionId} size={13} />,
                          })),
                        ]}
                        onChange={(event) => {
                          const next = event.target.value;
                          if (next === draft.instanceId) return;
                          edit({
                            instanceId: next,
                            remoteProjectId: '',
                            issueTypeId: null,
                            issueTypeName: null,
                          });
                        }}
                      />
                    </span>
                  )}
                  {instance === null ? null : (
                    <span className="min-w-[160px] flex-1">
                      <Select
                        aria-label={t('lineItem.target.project')}
                        /* 5 Nov 2026 · ⚠️ sized by the span: `Select` puts `className` on its button
                       and keeps its own wrapper `w-full`, which pushed this onto a line of its own. */
                        className="h-[30px] text-[12.5px]"
                        disabled={!canConfigure}
                        searchable
                        searchPlaceholder={t('rule.searchValues')}
                        emptyLabel={t('lineItem.target.noProjects')}
                        value={draft.remoteProjectId}
                        options={[
                          ...(draft.remoteProjectId === ''
                            ? [
                                {
                                  value: '',
                                  label: t('lineItem.target.pickProject'),
                                  disabled: true,
                                },
                              ]
                            : []),
                          ...projectChoices,
                        ]}
                        onChange={(event) => {
                          const next = event.target.value;
                          if (next === draft.remoteProjectId) return;
                          /* Another project has other issue types; the saved one brings its own back. */
                          edit(
                            target !== null &&
                              target.instanceId === instance.instanceId &&
                              target.remoteProjectId === next
                              ? {
                                  remoteProjectId: next,
                                  writesEnabled: target.writesEnabled,
                                  issueTypeId: target.issueTypeId,
                                  issueTypeName: target.issueTypeName,
                                }
                              : { remoteProjectId: next, issueTypeId: null, issueTypeName: null },
                          );
                        }}
                      />
                    </span>
                  )}
                </div>
              ) : null}
              {/* ⚠️ 5 Nov 2026 · writing back sits on the source's own line, and the type a new issue
                gets is behind the pencil after it — the product owner: it is a detail of writing
                back, not a row of its own. */}
              {pickedProject ? (
                <div className="flex h-[30px] shrink-0 items-center gap-1">
                  <label className="flex items-center gap-2 text-[12.5px]">
                    <Switch
                      checked={draft.writesEnabled}
                      disabled={!canConfigure}
                      label={t('lineItem.target.writes')}
                      onCheckedChange={(writesEnabled) => {
                        edit({ writesEnabled });
                      }}
                    />
                    {t('lineItem.target.writes')}
                  </label>
                  <Popover
                    open={typeOpen}
                    onClose={() => {
                      setTypeOpen(false);
                    }}
                    label={t('lineItem.target.issueTypeLabel')}
                    width={300}
                    align="start"
                    content={
                      <div className="flex flex-col gap-2">
                        {canConfigure ? (
                          <IssueTypePicker
                            chosen={
                              draft.issueTypeId === null
                                ? null
                                : { id: draft.issueTypeId, name: draft.issueTypeName }
                            }
                            types={issueTypes}
                            onChange={(type) => {
                              edit({ issueTypeId: type.id, issueTypeName: type.name });
                            }}
                          />
                        ) : (
                          <span className="text-[12.5px]">{typeSentence}</span>
                        )}
                        {draft.issueTypeName !== null && EPIC.test(draft.issueTypeName) ? (
                          <span className="text-muted-foreground text-[11.5px]">
                            {t('lineItem.target.epicHint')}
                          </span>
                        ) : null}
                      </div>
                    }
                  >
                    <Tooltip content={typeSentence}>
                      <Button
                        variant="ghost"
                        size="icon-sm"
                        className="text-muted-foreground"
                        aria-label={t('lineItem.target.issueTypeLabel')}
                        aria-expanded={typeOpen}
                        onClick={() => {
                          setTypeOpen((was) => !was);
                        }}
                      >
                        <Icon name="pencil" size={13} />
                      </Button>
                    </Tooltip>
                  </Popover>
                </div>
              ) : null}
            </div>
            {draft.sync && onSavedTarget && !target.reachable ? (
              <p className="flex items-start gap-1.5 text-[11.5px] text-amber-700 dark:text-amber-400">
                <Icon name="triangle-alert" size={13} className="mt-0.5 shrink-0" />
                {t('lineItem.target.unreachableBody', { key: target.remoteKey })}
              </p>
            ) : null}
          </div>
        ) : null}

        {/* 2 · by hand or by rule — a small toggle, not a full-width one. */}
        <RowLabel>{t('lineItem.assignment')}</RowLabel>
        <Segmented
          label={t('lineItem.assignment')}
          className="self-start justify-self-start"
          disabled={!canManage}
          value={draft.assignment}
          options={[
            { value: 'manual', label: t('lineItem.mode.manual') },
            { value: 'rule', label: t('lineItem.mode.rule') },
          ]}
          onChange={(next) => {
            /* R1 · back to manual is always allowed; into rule mode only while open. */
            if (next === 'rule' && !canConfigure && saved.assignment !== 'rule') return;
            edit({ assignment: next });
          }}
        />

        {/* 3 · the conditions, only by rule — the full width of the card, under the mode. */}
        {draft.assignment === 'rule' ? (
          <div className="col-span-2 min-w-0 @max-[560px]:col-span-1">
            <RuleEditor
              rule={draft.rule}
              options={conditionOptions}
              remote={pickedProject}
              immediate
              canManage={canConfigure}
              onChange={(next) => {
                edit({ rule: next });
              }}
            />
          </div>
        ) : null}
      </div>

      {/* 4 · the footer. */}
      {footer !== undefined || canManage ? (
        <div className="bg-muted/30 flex flex-wrap items-center gap-x-3 gap-y-1.5 border-t px-3 py-2">
          {footer}
          <div className="flex-1" />
          {canManage ? (
            <div className="flex items-center gap-1.5">
              <Button
                variant="ghost"
                size="sm"
                disabled={!dirty || busy}
                onClick={() => {
                  setDraft(saved);
                }}
              >
                {t('common.cancel')}
              </Button>
              <Button size="sm" disabled={!dirty || !valid || busy} onClick={save}>
                {t('common.save')}
              </Button>
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

/** A setting's name in the card's left column, on the line of its 30px control. */
function RowLabel({ children }: { readonly children: ReactNode }) {
  return (
    <span className="text-muted-foreground flex h-[30px] items-center text-[12px] @max-[560px]:h-auto">
      {children}
    </span>
  );
}

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ ux-pass-2 · what type an issue written to the tracker gets.               │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * *"Also make the task type (epic) selectable, the user should be able to define what type the
 * task will be in the issue tracker."* The API always took a choice (`setLineItemTargetSchema`);
 * the first sync's `chooseIssueType` only fills a line item that has none, so a choice made here
 * stands.
 *
 * ⚠️ **Sub-tasks are drawn disabled, with their reason**, not dropped: Jira refuses a sub-task
 * created without a parent, and an outbound task has none.
 *
 * ⚠️ **The chosen type stays an option even when the list lacks it** — a type the project has since
 * removed, or a list that failed to load — so the picker never shows a saved choice as empty.
 */
function IssueTypePicker({
  chosen,
  types,
  onChange,
}: {
  readonly chosen: { readonly id: string; readonly name: string | null } | null;
  readonly types: readonly RemoteIssueType[] | null | 'failed';
  readonly onChange: (type: RemoteIssueType) => void;
}) {
  const t = useT();
  const listed: readonly RemoteIssueType[] = types === null || types === 'failed' ? [] : types;
  const choices = listed.map((type) => ({
    value: type.id,
    label: type.subtask ? t('lineItem.target.issueTypeSubtask', { type: type.name }) : type.name,
    disabled: type.subtask && type.id !== chosen?.id,
  }));
  if (chosen !== null && !listed.some((type) => type.id === chosen.id)) {
    choices.unshift({ value: chosen.id, label: chosen.name ?? chosen.id, disabled: false });
  }

  return (
    <span className="flex flex-col gap-1.5 text-[12px]">
      <span className="text-muted-foreground">{t('lineItem.target.issueTypeLabel')}</span>
      <Select
        aria-label={t('lineItem.target.issueTypeLabel')}
        className="h-[30px] text-[12px]"
        searchable
        searchPlaceholder={t('rule.searchValues')}
        emptyLabel={
          types === 'failed'
            ? t('lineItem.target.issueTypesFailed')
            : types === null
              ? t('lineItem.target.issueTypesLoading')
              : t('lineItem.target.noIssueTypes')
        }
        value={chosen?.id ?? ''}
        options={[
          ...(chosen === null
            ? [{ value: '', label: t('lineItem.target.issueTypeAuto'), disabled: true }]
            : []),
          ...choices,
        ]}
        onChange={(event) => {
          const picked = listed.find((type) => type.id === event.target.value);
          if (picked === undefined || picked.id === chosen?.id) return;
          onChange(picked);
        }}
      />
      {types === 'failed' ? (
        <span className="text-muted-foreground">{t('lineItem.target.issueTypesFailed')}</span>
      ) : null}
    </span>
  );
}
